import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import { createStripeCheckout } from '../payment/StripeCheckout';
import { TOKEN_PACKAGES, TokenPackage } from '../../constants/tokenPackages';

/**
 * Wallet component that allows users to manage their token balance
 * 
 * Main features:
 * 1. View current token balance
 * 2. Purchase tokens
 * 3. Manage withdrawals
 */
function Wallet() {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  
  // Payment state
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  /**
   * Handle buying tokens through payment
   */
  const handleBuyTokens = async (amount: number, price: number) => {
    // Check if user is authenticated before proceeding
    if (!user.email || !user.token) {
      // If not authenticated, show an alert and redirect to login
      alert(t('errors.loginToPurchase', 'Please log in or create an account before purchasing tokens.'));
      
      // Redirect to login page
      setTimeout(() => {
        navigate('/login?redirect=/wallet');
      }, 2000);
      
      return;
    }
    
    // Prevent multiple clicks
    if (isProcessingPayment) {
      return;
    }
    
    // Get user email from current context to ensure we're using the correct one
    const currentUserEmail = user.email;
    
    // Use the new utility function for creating checkout
    createStripeCheckout({
      tokenAmount: amount,
      priceAmount: price,
      userEmail: currentUserEmail, // Use the current user email from context
      onStart: () => {
        setIsProcessingPayment(true);
      },
      onError: (errorMessage) => {
        setIsProcessingPayment(false);
        alert(errorMessage);
      }
    });
  };

  // Calculate savings percentage
  const calculateSavings = (tokens: number, price: number) => {
    const baseRate = TOKEN_PACKAGES[0].price / TOKEN_PACKAGES[0].tokens; // Price per token in the smallest package
    const packageRate = price / tokens;
    const savingsPercent = Math.round((1 - (packageRate / baseRate)) * 100);
    return savingsPercent;
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          
          {/* Balance Card */}
          <div className="card mb-4 bg-body-tertiary border-0">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col">
                  <h5 className="mb-0">{t('settings.tokenWallet', 'Token Wallet')}</h5>
                  <p className="text-muted mb-0">{t('settings.manageYourTokens', 'Manage your tokens and purchases')}</p>
                </div>
                <div className="col-auto">
                  <div className="d-flex align-items-center">
                    <span className="fs-5 fw-bold me-2">{user.tokenBalance}</span>
                    <span className="badge bg-primary rounded-pill px-3 py-2">{t('settings.tokens', 'Tokens')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="h4 mb-0">{t('settings.purchaseTokens', 'Purchase Tokens')}</h2>
            <div className="d-flex gap-2">
              <Link to="/token-transactions" className="btn btn-outline-primary btn-sm">
                <i className="bi bi-list-ul me-2"></i>
                {t('settings.tokenTransactions', 'Token Transactions')}
              </Link>
              <Link to="/purchase-history" className="btn btn-outline-primary btn-sm">
                <i className="bi bi-clock-history me-2"></i>
                {t('settings.purchaseHistory', 'Purchase History')}
              </Link>
            </div>
          </div>
          
          {/* Token Packages */}
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4 mb-5">
            {TOKEN_PACKAGES.map((pkg: TokenPackage) => (
              <div className="col" key={pkg.tokens}>
                <div className={`card h-100 ${pkg.popular ? 'border-primary' : pkg.bestValue ? 'border-success' : 'border'}`}>
                  {pkg.popular && (
                    <div className="card-header text-center text-white bg-primary py-2">
                      <strong>{t('settings.mostPopular', 'MOST POPULAR')}</strong>
                    </div>
                  )}
                  {pkg.bestValue && (
                    <div className="card-header text-center text-white bg-success py-2">
                      <strong>{t('settings.bestValue', 'BEST VALUE')}</strong>
                    </div>
                  )}
                  <div className="card-body d-flex flex-column">
                    <div className="text-center mb-3">
                      <h3 className="fw-bold">{pkg.tokens.toLocaleString()}</h3>
                      <p className="text-muted mb-0">{t('settings.tokens', 'Tokens')}</p>
                    </div>
                    
                    <div className="d-flex justify-content-center mb-3">
                      <span className="fs-3 fw-bold">${pkg.price.toFixed(2)}</span>
                    </div>
                    
                    {pkg.tokens > TOKEN_PACKAGES[0].tokens && (
                      <div className="mb-3 text-center">
                        <span className="badge bg-success px-2 py-1">
                          {t('settings.savePercent', 'Save')} {calculateSavings(pkg.tokens, pkg.price)}%
                        </span>
                      </div>
                    )}
                    
                    <p className="text-center mb-0 text-muted small">
                      {t('settings.pricePerToken', 'Price per token')}: ${pkg.pricePerToken.toFixed(2)}
                    </p>
                    
                    <div className="mt-auto pt-3">
                      <button 
                        className={`btn ${pkg.popular ? 'btn-primary' : pkg.bestValue ? 'btn-success' : 'btn-outline-primary'} w-100`}
                        onClick={() => handleBuyTokens(pkg.tokens, pkg.price)}
                        disabled={isProcessingPayment}
                      >
                        {isProcessingPayment ? (
                          <span>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('common.processing', 'Processing...')}
                          </span>
                        ) : (
                          t('settings.buyNow', 'Buy Now')
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
           
          {/* Currency Conversion Information */}
          <div className="card bg-body-tertiary border-0 mb-5">
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-8">
                  <h3 className="h5 mb-3">
                    <i className="bi bi-currency-exchange me-2 text-primary"></i>
                    {t('settings.currencyConversion', 'Currency Conversion')}
                  </h3>
                  <p className="mb-2">
                    {t('settings.currencyConversionDesc', 'Stripe automatically handles currency conversion for international users')}
                  </p>
                  <p className="text-muted mb-4">
                    {t('settings.currencyConversionDetails', 'All transactions on our platform are processed in USD. If your bank account uses a different currency, Stripe will automatically convert the funds using mid-market exchange rates with a small margin. This ensures you receive payments in your local currency without any additional effort on your part.')}
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <a 
                      href="https://stripe.com/docs/currencies/conversions" 
                      className="btn btn-outline-secondary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-info-circle me-2"></i>
                      {t('settings.learnMoreAboutConversion', 'Learn More About Currency Conversion')}
                    </a>
                  </div>
                </div>
                <div className="col-md-4 d-none d-md-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <i className="bi bi-currency-exchange text-primary" style={{ fontSize: '4rem' }}></i>
                    <p className="mt-2 mb-0 small text-muted">
                      {t('settings.automaticConversion', 'Automatic currency conversion by Stripe')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
                   
          {/* Features */}
          <div className="row mb-5">
            <div className="col-md-4 mb-4 mb-md-0">
              <div className="text-center p-3 rounded-3 card h-100 bg-body-tertiary">
                <i className="bi bi-shield-check fs-1 text-primary mb-3"></i>
                <h5>{t('settings.securePayments', 'Secure Payments')}</h5>
                <p className="text-muted">
                  {t('settings.securePaymentsDesc', 'All transactions are secured with SSL encryption')}
                </p>
              </div>
            </div>
            <div className="col-md-4 mb-4 mb-md-0">
              <div className="text-center p-3 rounded-3 card h-100 bg-body-tertiary">
                <i className="bi bi-lightning-charge fs-1 text-primary mb-3"></i>
                <h5>{t('settings.instantDelivery', 'Instant Delivery')}</h5>
                <p className="text-muted">
                  {t('settings.instantDeliveryDesc', 'Tokens are added to your account immediately')}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center p-3 rounded-3 card h-100 bg-body-tertiary">
                <i className="bi bi-currency-exchange fs-1 text-primary mb-3"></i>
                <h5>{t('settings.flexibleUsage', 'Flexible Usage')}</h5>
                <p className="text-muted">
                  {t('settings.flexibleUsageDesc', 'Use tokens for any feature or convert them back to cash anytime')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Withdrawals Section */}
          <div className="card bg-body-tertiary border-0 mb-5">
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-8">
                  <h3 className="h5 mb-3">
                    <i className="bi bi-wallet2 me-2 text-primary"></i>
                    {t('settings.withdrawals', 'Withdrawals')}
                  </h3>
                  <p className="mb-2">
                    {t('settings.withdrawalsDesc', 'Convert your tokens into real money through Stripe Connect')}
                  </p>
                  <p className="text-muted mb-4">
                    {t('settings.stripeConnectDesc', 'Our platform uses Stripe Connect, a secure payment service that allows you to receive funds directly to your bank account. Set up your Stripe Connect account once, then easily withdraw your tokens whenever you want.')}
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Link to="/withdrawal-setup" className="btn btn-primary">
                      <i className="bi bi-bank me-2"></i>
                      {t('settings.goToWithdrawals', 'Go To Withdrawals')}
                    </Link>
                    <a 
                      href="https://stripe.com/connect/payouts" 
                      className="btn btn-outline-secondary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-question-circle me-2"></i>
                      {t('settings.learnMore', 'Learn More')}
                    </a>
                  </div>
                </div>
                <div className="col-md-4 d-none d-md-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <i className="bi bi-currency-exchange text-primary" style={{ fontSize: '4rem' }}></i>
                    <p className="mt-2 mb-0 small text-muted">
                      {t('settings.instantTransfers', 'Fast and secure transfers')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Wallet; 